import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Edit2, Check, X, AlertTriangle, Clock, Trash2 } from 'lucide-react';
import { usePageView } from '../hooks/usePageView';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = ({ user, setUser }) => {
  usePageView('profile');
  
  const [editingUsername, setEditingUsername] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [editingPicture, setEditingPicture] = useState(false);
  const [newUsername, setNewUsername] = useState(user?.username || '');
  const [newName, setNewName] = useState(user?.name || '');
  const [newPicture, setNewPicture] = useState(user?.picture || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletionType, setDeletionType] = useState(null);
  const [deletionStatus, setDeletionStatus] = useState(null);

  // Check for pending deletion status
  const checkDeletionStatus = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/security/deletion-status`, {
        withCredentials: true
      });
      setDeletionStatus(response.data);
    } catch (error) {
      console.error('Error checking deletion status:', error);
    }
  }, []);

  const loadUserStats = React.useCallback(async () => {
    if (!user?._id) {
      setLoadingStats(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/users/${user._id}/profile`,
        { withCredentials: true }
      );
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [user?._id]);

  React.useEffect(() => {
    loadUserStats();
    checkDeletionStatus();
  }, [loadUserStats, checkDeletionStatus]);

  const handleUpdateUsername = async () => {
    if (!newUsername || newUsername === user.username) {
      setEditingUsername(false);
      return;
    }

    // Validate username format
    const usernameRegex = /^[a-zA-Z0-9_]{4,20}$/;
    if (!usernameRegex.test(newUsername)) {
      setError('Username must be 4-20 characters (letters, numbers, underscores only)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(
        `${API}/auth/username`,
        { username: newUsername },
        { withCredentials: true }
      );

      // Update user state
      setUser({ ...user, username: newUsername });
      setSuccess('Username updated successfully!');
      setEditingUsername(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error updating username');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (field) => {
    const updates = {};
    
    if (field === 'name') {
      if (!newName || newName === user.name) {
        setEditingName(false);
        return;
      }
      // Validate name length
      if (newName.trim().length < 4 || newName.trim().length > 100) {
        setError('Name must be 4-100 characters');
        return;
      }
      updates.name = newName;
    } else if (field === 'picture') {
      if (!newPicture || newPicture === user.picture) {
        setEditingPicture(false);
        return;
      }
      updates.picture = newPicture;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.put(
        `${API}/auth/profile`,
        updates,
        { withCredentials: true }
      );

      // Update user state
      setUser({ ...user, ...updates });
      setSuccess(`${field === 'name' ? 'Name' : 'Picture'} updated successfully!`);
      
      if (field === 'name') setEditingName(false);
      if (field === 'picture') setEditingPicture(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || `Error updating ${field}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (field) => {
    if (field === 'username') {
      setNewUsername(user?.username || '');
      setEditingUsername(false);
    } else if (field === 'name') {
      setNewName(user?.name || '');
      setEditingName(false);
    } else if (field === 'picture') {
      setNewPicture(user?.picture || '');
      setEditingPicture(false);
    }
    setError('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      setError('Please type DELETE MY ACCOUNT to confirm');
      return;
    }

    if (!deletionType) {
      setError('Please select a deletion option');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/security/delete-account`,
        { 
          deletion_type: deletionType,
          confirmation: deleteConfirmText 
        },
        { withCredentials: true }
      );
      
      if (deletionType === 'immediate') {
        toast.success('Your account has been deleted. Goodbye! 🐑');
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        setUser(null);
        window.location.href = '/';
      } else {
        toast.success('Account scheduled for deletion in 30 days');
        setShowDeleteConfirm(false);
        setDeleteConfirmText('');
        setDeletionType(null);
        checkDeletionStatus();
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error processing deletion request');
      setLoading(false);
    }
  };

  const handleCancelDeletion = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/security/cancel-deletion`, {
        confirmation: 'CANCEL'
      }, { withCredentials: true });
      toast.success('Account deletion cancelled!');
      checkDeletionStatus();
    } catch (error) {
      toast.error('Failed to cancel deletion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <Navigation user={user} setUser={setUser} currentPage="profile" />
      
      <div className="profile-content">
        <div className="page-header">
          <h1 data-testid="profile-title">Profile</h1>
          <p>Manage your account settings</p>
        </div>

        <Card className="profile-card">
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="profile-card-content">
            <div className="profile-section">
              <Avatar className="profile-avatar">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback><User size={48} /></AvatarFallback>
              </Avatar>

              <div className="profile-info">
                <div className="info-row">
                  <Label>Profile Picture URL</Label>
                  {!editingPicture ? (
                    <div className="field-display">
                      <div className="info-value picture-value">{user?.picture}</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPicture(true)}
                        data-testid="edit-picture-btn"
                      >
                        <Edit2 size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="field-edit">
                      <Input
                        value={newPicture}
                        onChange={(e) => {
                          setNewPicture(e.target.value);
                          setError('');
                        }}
                        placeholder="https://example.com/image.jpg"
                        data-testid="picture-input"
                        autoFocus
                      />
                      <div className="edit-actions">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateProfile('picture')}
                          disabled={loading}
                          data-testid="save-picture-btn"
                        >
                          <Check size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancel('picture')}
                          disabled={loading}
                          data-testid="cancel-picture-btn"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="info-row">
                  <Label>Name</Label>
                  {!editingName ? (
                    <div className="field-display">
                      <div className="info-value">{user?.name}</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingName(true)}
                        data-testid="edit-name-btn"
                      >
                        <Edit2 size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="field-edit">
                      <Input
                        value={newName}
                        onChange={(e) => {
                          setNewName(e.target.value);
                          setError('');
                        }}
                        placeholder="Your name"
                        data-testid="name-input"
                        autoFocus
                      />
                      <div className="edit-actions">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateProfile('name')}
                          disabled={loading}
                          data-testid="save-name-btn"
                        >
                          <Check size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancel('name')}
                          disabled={loading}
                          data-testid="cancel-name-btn"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                  {!editingName && (
                    <div className="usage-note">
                      <strong>How it's used:</strong> Displayed on your reviews and posts in the social feed. Visible to all users.
                    </div>
                  )}
                </div>

                <div className="info-row">
                  <Label>Email</Label>
                  <div className="info-value email-value">{user?.email}</div>
                  <div className="info-note">Email cannot be changed</div>
                  <div className="usage-note">
                    <strong>How it's used:</strong> Used for login via Google. Never displayed publicly or shared with other users.
                  </div>
                </div>

                <div className="info-row">
                  <Label>Username (Handle)</Label>
                  {!editingUsername ? (
                    <div className="field-display">
                      <div className="info-value username-value">@{user?.username}</div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingUsername(true)}
                        data-testid="edit-username-btn"
                      >
                        <Edit2 size={16} />
                      </Button>
                    </div>
                  ) : (
                    <div className="field-edit">
                      <div className="username-input-wrapper">
                        <span className="username-prefix">@</span>
                        <Input
                          value={newUsername}
                          onChange={(e) => {
                            setNewUsername(e.target.value);
                            setError('');
                          }}
                          placeholder="username"
                          data-testid="username-input"
                          autoFocus
                        />
                      </div>
                      <div className="edit-actions">
                        <Button
                          size="sm"
                          onClick={handleUpdateUsername}
                          disabled={loading}
                          data-testid="save-username-btn"
                        >
                          <Check size={16} />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancel('username')}
                          disabled={loading}
                          data-testid="cancel-username-btn"
                        >
                          <X size={16} />
                        </Button>
                      </div>
                    </div>
                  )}
                  {!editingUsername && (
                    <div className="usage-note">
                      <strong>How it's used:</strong> Your unique handle for finding friends and appearing in search. Displayed on your profile page and visible to all users.
                    </div>
                  )}
                </div>

                {error && (
                  <div className="message error-message" data-testid="error-message">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="message success-message" data-testid="success-message">
                    {success}
                  </div>
                )}

                <div className="profile-rules">
                  <h4>Profile Requirements:</h4>
                  <ul>
                    <li><strong>Name:</strong> 4-100 characters</li>
                    <li><strong>Username:</strong> 4-20 characters (letters, numbers, underscores only, must be unique)</li>
                    <li><strong>Picture:</strong> Valid image URL (must start with http:// or https://)</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card">
          <CardHeader>
            <CardTitle>Your BaadTV Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <div className="loading-stats">Loading stats...</div>
            ) : stats ? (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">
                    {stats.average_rating !== null && stats.average_rating !== undefined 
                      ? stats.average_rating 
                      : 'N/A'}
                  </div>
                  <div className="stat-label">Avg Rating</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">
                    {stats.highest_rating !== null && stats.highest_rating !== undefined 
                      ? stats.highest_rating 
                      : 'N/A'}
                  </div>
                  <div className="stat-label">Highest Rating</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">
                    {stats.lowest_rating !== null && stats.lowest_rating !== undefined 
                      ? stats.lowest_rating 
                      : 'N/A'}
                  </div>
                  <div className="stat-label">Lowest Rating</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">
                    {stats.total_reviews !== undefined ? stats.total_reviews : 0}
                  </div>
                  <div className="stat-label">Total Reviews</div>
                </div>
              </div>
            ) : (
              <div className="no-stats">No stats available</div>
            )}
          </CardContent>
        </Card>

        <Card className="danger-zone-card">
          <CardHeader>
            <div className="danger-header">
              <AlertTriangle className="danger-icon" />
              <CardTitle>Danger Zone</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="danger-content">
              {/* Pending Deletion Warning */}
              {deletionStatus?.pending_deletion && (
                <div className="pending-deletion-warning">
                  <AlertTriangle size={20} />
                  <div>
                    <strong>Account Deletion Scheduled</strong>
                    <p>Your account will be deleted in {deletionStatus.days_remaining} days 
                       ({new Date(deletionStatus.scheduled_deletion_date).toLocaleDateString()}).</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCancelDeletion}
                      disabled={loading}
                    >
                      {loading ? 'Cancelling...' : 'Cancel Deletion'}
                    </Button>
                  </div>
                </div>
              )}

              {!deletionStatus?.pending_deletion && (
                <>
                  <div className="danger-description">
                    <h4>Delete Account</h4>
                    <p>
                      This action will permanently delete all your data including:
                    </p>
                    <ul>
                      <li>All your shows and ratings</li>
                      <li>All your reviews and posts</li>
                      <li>Your friends and followers</li>
                      <li>All your account data</li>
                    </ul>
                  </div>
                  
                  {!showDeleteConfirm ? (
                    <Button
                      variant="destructive"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="delete-btn"
                      data-testid="show-delete-btn"
                    >
                      Start Deletion Process
                    </Button>
                  ) : !deletionType ? (
                    <div className="deletion-options">
                      <p className="options-label">Choose deletion method:</p>
                      <button 
                        className="deletion-option scheduled"
                        onClick={() => setDeletionType('scheduled')}
                      >
                        <Clock size={20} />
                        <div>
                          <strong>30-Day Grace Period</strong>
                          <span>Account deleted after 30 days. Cancel anytime.</span>
                        </div>
                      </button>
                      <button 
                        className="deletion-option immediate"
                        onClick={() => setDeletionType('immediate')}
                      >
                        <Trash2 size={20} />
                        <div>
                          <strong>Delete Immediately</strong>
                          <span>Permanently delete right now. Cannot be undone.</span>
                        </div>
                      </button>
                      <Button
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(false)}
                        className="cancel-options-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <div className="delete-confirm">
                      <div className={`deletion-type-badge ${deletionType}`}>
                        {deletionType === 'immediate' ? (
                          <><Trash2 size={14} /> Immediate Deletion</>
                        ) : (
                          <><Clock size={14} /> 30-Day Grace Period</>
                        )}
                      </div>
                      <p className="confirm-instruction">
                        Type <strong>DELETE MY ACCOUNT</strong> to confirm:
                      </p>
                      <Input
                        value={deleteConfirmText}
                        onChange={(e) => {
                          setDeleteConfirmText(e.target.value);
                          setError('');
                        }}
                        placeholder="DELETE MY ACCOUNT"
                        className="confirm-input"
                        data-testid="delete-confirm-input"
                      />
                      <div className="confirm-actions">
                        <Button
                          variant="destructive"
                          onClick={handleDeleteAccount}
                          disabled={loading || deleteConfirmText !== 'DELETE MY ACCOUNT'}
                          data-testid="confirm-delete-btn"
                        >
                          {loading ? 'Processing...' : deletionType === 'immediate' ? 'Delete Now' : 'Schedule Deletion'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setDeletionType(null);
                            setDeleteConfirmText('');
                            setError('');
                          }}
                          disabled={loading}
                          data-testid="cancel-delete-btn"
                        >
                          Back
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <style jsx>{`
        .profile-container {
          min-height: 100vh;
          background: #121212;
        }

        .profile-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2.5rem;
        }

        .page-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .page-header p {
          color: #b0b0b0;
          font-size: 1.1rem;
        }

        .profile-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          border-radius: 8px;
        }

        .profile-card :global(h3) {
          color: #ffffff;
        }

        .stats-card {
          background: #1a1a1a;
          border: 1px solid #333333;
          border-radius: 8px;
          margin-top: 2rem;
        }

        .stats-card :global(h3) {
          color: #ffffff;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }

        .stat-card {
          background: #2a2a2a;
          border: 1px solid #333333;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #E62F88;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #b0b0b0;
          font-weight: 500;
        }

        .loading-stats,
        .no-stats {
          text-align: center;
          padding: 2rem;
          color: #b0b0b0;
        }

        .profile-card-content {
          padding: 2rem;
        }

        .profile-section {
          display: flex;
          gap: 2rem;
          align-items: flex-start;
        }

        .profile-avatar {
          width: 120px;
          height: 120px;
          flex-shrink: 0;
        }

        .profile-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .info-row {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .info-row :global(label) {
          color: #b0b0b0;
          font-size: 0.9rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          color: #ffffff;
          font-size: 1.1rem;
        }

        .email-value {
          color: #b0b0b0;
          font-size: 1rem;
        }

        .username-value {
          color: #E62F88;
          font-weight: 600;
        }

        .picture-value {
          font-size: 0.9rem;
          color: #b0b0b0;
          word-break: break-all;
        }

        .info-note {
          font-size: 0.85rem;
          color: #b0b0b0;
          font-style: italic;
          margin-top: 0.25rem;
        }

        .usage-note {
          font-size: 0.85rem;
          color: #b0b0b0;
          background: rgba(230, 47, 136, 0.05);
          border-left: 3px solid #E62F88;
          padding: 0.75rem;
          margin-top: 0.75rem;
          border-radius: 4px;
          line-height: 1.5;
        }

        .usage-note strong {
          color: #E62F88;
          display: block;
          margin-bottom: 0.25rem;
        }

        .field-display {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .field-display button {
          color: #E62F88;
          border-color: #E62F88;
          flex-shrink: 0;
        }

        .field-display button:hover {
          background: #E62F88;
          color: #000000;
        }

        .field-edit {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .username-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .username-prefix {
          color: #E62F88;
          font-size: 1.2rem;
          font-weight: 600;
        }

        .username-input-wrapper :global(input) {
          flex: 1;
          background: #000000;
          color: #ffffff;
          border: 1px solid #333333;
        }

        .field-edit :global(input) {
          background: #000000;
          color: #ffffff;
          border: 1px solid #333333;
        }

        .edit-actions {
          display: flex;
          gap: 0.5rem;
        }

        .edit-actions :global(button) {
          min-width: 40px;
          height: 40px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .message {
          padding: 0.75rem 1rem;
          border-radius: 6px;
          font-size: 0.95rem;
        }

        .error-message {
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
          border: 1px solid #dc3545;
        }

        .success-message {
          background: rgba(40, 167, 69, 0.1);
          color: #28a745;
          border: 1px solid #28a745;
        }

        .profile-rules {
          padding: 1rem;
          background: #2a2a2a;
          border: 1px solid #333333;
          border-radius: 6px;
          margin-top: 0.5rem;
        }

        .profile-rules h4 {
          color: #E62F88;
          font-size: 0.95rem;
          margin-bottom: 0.5rem;
        }

        .profile-rules ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .profile-rules li {
          color: #b0b0b0;
          font-size: 0.9rem;
          padding: 0.25rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .profile-rules li:before {
          content: "•";
          color: #E62F88;
          position: absolute;
          left: 0.5rem;
        }

        .profile-rules li strong {
          color: #E62F88;
        }

        .danger-zone-card {
          background: #1a1a1a;
          border: 2px solid #dc3545;
          border-radius: 8px;
          margin-top: 2rem;
        }

        .danger-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .danger-icon {
          color: #dc3545;
          width: 24px;
          height: 24px;
        }

        .danger-zone-card :global(h3) {
          color: #dc3545;
        }

        .danger-content {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .danger-description h4 {
          color: #ffffff;
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }

        .danger-description p {
          color: #b0b0b0;
          line-height: 1.6;
          margin-bottom: 0.5rem;
        }

        .danger-description ul {
          list-style-type: disc;
          padding-left: 1.5rem;
          color: #b0b0b0;
          line-height: 1.8;
        }

        .danger-description li {
          margin-bottom: 0.25rem;
        }

        .delete-btn {
          align-self: flex-start;
        }

        .pending-deletion-warning {
          display: flex;
          gap: 1rem;
          padding: 1rem;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid #ef4444;
          border-radius: 8px;
          color: #ef4444;
        }

        .pending-deletion-warning strong {
          color: #ffffff;
          display: block;
          margin-bottom: 0.25rem;
        }

        .pending-deletion-warning p {
          color: #b0b0b0;
          margin-bottom: 0.75rem;
          font-size: 0.9rem;
        }

        .deletion-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .options-label {
          color: #b0b0b0;
          margin-bottom: 0.25rem;
        }

        .deletion-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border-radius: 8px;
          border: 1px solid #333;
          background: #2a2a2a;
          cursor: pointer;
          text-align: left;
          transition: all 0.2s;
        }

        .deletion-option:hover {
          border-color: #E62F88;
        }

        .deletion-option.scheduled {
          color: #f59e0b;
        }

        .deletion-option.immediate {
          color: #ef4444;
        }

        .deletion-option strong {
          color: #ffffff;
          display: block;
          margin-bottom: 0.25rem;
        }

        .deletion-option span {
          color: #b0b0b0;
          font-size: 0.85rem;
        }

        .cancel-options-btn {
          align-self: flex-start;
          margin-top: 0.5rem;
        }

        .deletion-type-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          align-self: flex-start;
        }

        .deletion-type-badge.scheduled {
          background: rgba(245, 158, 11, 0.2);
          color: #f59e0b;
        }

        .deletion-type-badge.immediate {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .delete-confirm {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
          background: rgba(220, 53, 69, 0.1);
          border: 1px solid #dc3545;
          border-radius: 6px;
        }

        .confirm-instruction {
          color: #ffffff;
          font-size: 1rem;
        }

        .confirm-instruction strong {
          color: #dc3545;
          font-family: monospace;
        }

        .confirm-input {
          max-width: 300px;
        }

        .confirm-actions {
          display: flex;
          gap: 0.75rem;
        }

        @media (max-width: 768px) {
          .profile-content {
            padding: 1rem;
            padding-bottom: 100px;
          }

          .page-header {
            margin-bottom: 1.5rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1rem;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-number {
            font-size: 2rem;
          }

          .profile-section {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .profile-avatar {
            width: 100px;
            height: 100px;
          }

          .profile-info {
            width: 100%;
          }

          .field-display {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .field-display button {
            width: 100%;
          }

          .edit-actions {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Profile;
